import { useEffect, useMemo, useState } from 'react';
import { LiksDialog } from '../LiksDialog/LiksDialog';
import { LiksDocumentPreviewBody } from './LiksDocumentPreviewBody';
import { LiksDocumentPreviewHeader } from './LiksDocumentPreviewHeader';
import { resolvePreviewKind } from './documentPreviewKind';
import type { LiksDocumentPreviewModalProps } from './LiksDocumentPreviewModal.types';
import './LiksDocumentPreviewModal.css';

export type {
  LiksDocumentPreviewItem,
  LiksDocumentPreviewModalProps,
} from './LiksDocumentPreviewModal.types';

export function LiksDocumentPreviewModal({
  isOpen,
  items,
  activeIndex,
  onActiveIndexChange,
  displayTitle,
  blob,
  mimeType,
  fileName,
  isLoading,
  isError,
  isInaccessible,
  isDownloadSuccess,
  closeLabel,
  loadingLabel,
  errorLabel,
  inaccessibleLabel,
  unavailableLabel,
  notReadyLabel,
  onClose,
}: LiksDocumentPreviewModalProps) {
  const [decodedText, setDecodedText] = useState<{
    blob: Blob;
    text: string;
  } | null>(null);

  const activeItem = items[activeIndex] ?? items[0];
  const isActiveReady = activeItem?.isReadyForPreview ?? false;
  const hasMultipleItems = items.length > 1;
  const previewKind = resolvePreviewKind(blob?.type || mimeType, fileName);

  const objectUrl = useMemo(() => {
    if (!blob) {
      return null;
    }
    return URL.createObjectURL(blob);
  }, [blob]);

  useEffect(() => {
    if (!objectUrl) {
      return;
    }
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  useEffect(() => {
    if (!isOpen || !hasMultipleItems) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onActiveIndexChange(Math.max(0, activeIndex - 1));
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onActiveIndexChange(Math.min(items.length - 1, activeIndex + 1));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    activeIndex,
    hasMultipleItems,
    isOpen,
    items.length,
    onActiveIndexChange,
  ]);

  useEffect(() => {
    if (!blob || (previewKind !== 'markdown' && previewKind !== 'text')) {
      return;
    }

    let cancelled = false;
    void blob.text().then((text) => {
      if (!cancelled) {
        setDecodedText({ blob, text });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [blob, previewKind]);

  const blobText =
    blob != null && decodedText?.blob === blob ? decodedText.text : null;

  const canGoPrevious = activeIndex > 0;
  const canGoNext = activeIndex < items.length - 1;

  return (
    <LiksDialog
      isOpen={isOpen}
      onClose={onClose}
      title={displayTitle}
      size="xl"
      className="liks-document-preview"
      bodyClassName="liks-document-preview__body"
      closeLabel={closeLabel}
      headerActions={
        hasMultipleItems ? (
          <LiksDocumentPreviewHeader
            hasMultipleItems={hasMultipleItems}
            activeIndex={activeIndex}
            itemCount={items.length}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            onPrevious={() => onActiveIndexChange(Math.max(0, activeIndex - 1))}
            onNext={() =>
              onActiveIndexChange(Math.min(items.length - 1, activeIndex + 1))
            }
          />
        ) : undefined
      }
    >
      <LiksDocumentPreviewBody
        isActiveReady={isActiveReady}
        isLoading={isLoading}
        isError={isError}
        isInaccessible={isInaccessible}
        isDownloadSuccess={isDownloadSuccess}
        previewKind={previewKind}
        blobText={blobText}
        objectUrl={objectUrl}
        displayTitle={displayTitle}
        loadingLabel={loadingLabel}
        errorLabel={errorLabel}
        inaccessibleLabel={inaccessibleLabel}
        unavailableLabel={unavailableLabel}
        notReadyLabel={notReadyLabel}
      />
    </LiksDialog>
  );
}
